import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { proxyListAPI } from '../services/api';

function ProxyListDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isCreateMode = id === 'create';

  const [loading, setLoading] = useState(!isCreateMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    proxies: [],
    tags: [],
    isActive: true
  });

  const [proxyInput, setProxyInput] = useState({ url: '', name: '', isActive: true });
  const [tagInput, setTagInput] = useState('');
  const [bulkProxyInput, setBulkProxyInput] = useState('');
  const [showBulkImport, setShowBulkImport] = useState(false);

  useEffect(() => {
    if (!isCreateMode) {
      fetchProxyList();
    }
  }, [id]);

  const fetchProxyList = async () => {
    try {
      setLoading(true);
      const response = await proxyListAPI.getById(id);
      const proxyList = response.data;

      setFormData({
        ...proxyList,
        tags: proxyList.tags || [],
        proxies: proxyList.proxies || []
      });
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (isCreateMode) {
        await proxyListAPI.create(formData);
      } else {
        await proxyListAPI.update(id, formData);
      }
      navigate('/proxy-lists');
    } catch (err) {
      alert('Failed to save proxy list: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const addProxy = () => {
    if (proxyInput.url) {
      setFormData(prev => ({
        ...prev,
        proxies: [...prev.proxies, { ...proxyInput }]
      }));
      setProxyInput({ url: '', name: '', isActive: true });
    }
  };

  const handleBulkImport = () => {
    if (!bulkProxyInput.trim()) return;

    const lines = bulkProxyInput.trim().split('\n');
    const newProxies = [];

    lines.forEach((line, index) => {
      const trimmedLine = line.trim();
      if (trimmedLine) {
        // Extract name from URL if available, or use index
        let proxyName = `Proxy-${formData.proxies.length + newProxies.length + 1}`;

        // Try to extract username as name
        try {
          const urlMatch = trimmedLine.match(/https?:\/\/([^:@]+)(?::([^@]+))?@/);
          if (urlMatch && urlMatch[1]) {
            proxyName = urlMatch[1];
          }
        } catch (e) {
          // Keep default name if parsing fails
        }

        newProxies.push({
          url: trimmedLine,
          name: proxyName,
          isActive: true
        });
      }
    });

    if (newProxies.length > 0) {
      setFormData(prev => ({
        ...prev,
        proxies: [...prev.proxies, ...newProxies]
      }));
      setBulkProxyInput('');
      setShowBulkImport(false);
      alert(`Successfully imported ${newProxies.length} proxies`);
    }
  };

  const removeProxy = (index) => {
    setFormData(prev => ({
      ...prev,
      proxies: prev.proxies.filter((_, i) => i !== index)
    }));
  };

  const toggleProxyActive = (index) => {
    setFormData(prev => ({
      ...prev,
      proxies: prev.proxies.map((proxy, i) =>
        i === index ? { ...proxy, isActive: !proxy.isActive } : proxy
      )
    }));
  };

  const addTag = () => {
    if (tagInput && !formData.tags.includes(tagInput)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput]
      }));
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };

  // No masking - show full proxy URLs for developer convenience
  const maskProxyUrl = (url) => {
    return url;
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="card">
        <h2>{isCreateMode ? 'Create' : 'Edit'} Proxy List</h2>

        {error && (
          <div className="alert alert-danger">
            Error: {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="e.g., US Residential Proxies"
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="3"
              placeholder="Description of this proxy list"
            />
          </div>

          <div className="form-group">
            <label>Status</label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleChange}
                style={{ width: 'auto' }}
              />
              Active
            </label>
          </div>

          <div className="form-group">
            <label>Proxies ({formData.proxies.length})</label>

            <div style={{ marginBottom: '1rem' }}>
              <button
                type="button"
                onClick={() => setShowBulkImport(!showBulkImport)}
                className="btn btn-secondary"
                style={{ marginBottom: '0.5rem' }}
              >
                {showBulkImport ? 'Hide Bulk Import' : 'Bulk Import Proxies'}
              </button>
            </div>

            {showBulkImport && (
              <div className="card" style={{ padding: '1rem', marginBottom: '1rem', backgroundColor: '#e8f4f8', border: '2px solid #3498db' }}>
                <h4 style={{ marginTop: 0 }}>Bulk Import Proxies</h4>
                <div className="form-group">
                  <label>Paste proxies (one per line)</label>
                  <textarea
                    value={bulkProxyInput}
                    onChange={(e) => setBulkProxyInput(e.target.value)}
                    rows="10"
                    placeholder="http://user:pass@proxy1.example.com:8080
http://user:pass@proxy2.example.com:8080
http://user:pass@proxy3.example.com:8080"
                    style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
                  />
                  <small className="text-muted">
                    Format: http://username:password@host:port (one proxy per line)
                  </small>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button type="button" onClick={handleBulkImport} className="btn btn-primary">
                    Import Proxies
                  </button>
                  <button type="button" onClick={() => { setBulkProxyInput(''); setShowBulkImport(false); }} className="btn btn-secondary">
                    Cancel
                  </button>
                </div>
              </div>
            )}

            <div className="card" style={{ padding: '1rem', marginBottom: '1rem', backgroundColor: '#f8f9fa' }}>
              <h4 style={{ marginTop: 0 }}>Add Single Proxy</h4>
              <div className="grid grid-3" style={{ gap: '0.5rem', marginBottom: '0.5rem' }}>
                <input
                  type="text"
                  placeholder="Proxy URL *"
                  value={proxyInput.url}
                  onChange={(e) => setProxyInput({ ...proxyInput, url: e.target.value })}
                />
                <input
                  type="text"
                  placeholder="Name (optional)"
                  value={proxyInput.name}
                  onChange={(e) => setProxyInput({ ...proxyInput, name: e.target.value })}
                />
                <button type="button" onClick={addProxy} className="btn btn-secondary">
                  Add Proxy
                </button>
              </div>
              <small className="text-muted">
                Example: http://user:pass@proxy.example.com:8080
              </small>
            </div>

            {formData.proxies.length > 0 ? (
              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>URL</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.proxies.map((proxy, index) => (
                      <tr key={index}>
                        <td>{proxy.name || '-'}</td>
                        <td>
                          <code style={{ fontSize: '0.85em' }}>
                            {maskProxyUrl(proxy.url)}
                          </code>
                        </td>
                        <td>
                          <button
                            type="button"
                            onClick={() => toggleProxyActive(index)}
                            className={`badge ${proxy.isActive ? 'badge-success' : 'badge-secondary'}`}
                            style={{ cursor: 'pointer', border: 'none' }}
                          >
                            {proxy.isActive ? 'Active' : 'Inactive'}
                          </button>
                        </td>
                        <td>
                          <button
                            type="button"
                            onClick={() => removeProxy(index)}
                            className="btn btn-sm btn-danger"
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="alert alert-info">
                No proxies added yet. Add proxies using the form above.
              </div>
            )}
          </div>

          <div className="form-group">
            <label>Tags</label>
            <div className="flex-row" style={{ gap: '0.5rem', marginBottom: '0.5rem' }}>
              <input
                type="text"
                placeholder="Add tag"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
              />
              <button type="button" onClick={addTag} className="btn btn-secondary">
                Add Tag
              </button>
            </div>
            {formData.tags.length > 0 && (
              <div style={{ marginTop: '0.5rem' }}>
                {formData.tags.map(tag => (
                  <span key={tag} className="badge badge-secondary mr-1" style={{ cursor: 'pointer' }}>
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      style={{ marginLeft: '0.5rem', background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="flex-row" style={{ gap: '0.5rem' }}>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : (isCreateMode ? 'Create' : 'Update')}
            </button>
            <button type="button" onClick={() => navigate('/proxy-lists')} className="btn btn-secondary">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ProxyListDetail;
